import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { FUNDER_QUIZ_STORAGE_KEY } from "../../lib/funderQuizMandate";

const FOUNDER_KEY = "clinq_get_started_v2";
const DISMISS_KEY = "clinq_quiz_nudge_dismissed";

function readDraft(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const o = JSON.parse(raw);
    return o && typeof o === "object" ? o : null;
  } catch (_) { return null; }
}

// Returns null (no nudge) or {kind, done, to, cta, text}
function detectNudge() {
  const f = readDraft(FOUNDER_KEY);
  if (f && (f.stage || f.revenueBand || (f.docs || []).length)) {
    if (f.completed_at) {
      return {
        kind: "founder-done",
        to: "/signup?role=founder",
        cta: "Sign up to see them",
        text: "Your Capital Readiness quiz is complete — your funder matches are waiting.",
      };
    }
    const done = [f.stage, f.revenueBand, f.isProfitable != null ? "p" : "", (f.docs || []).length ? "d" : ""].filter(Boolean).length;
    return {
      kind: "founder",
      to: "/get-started",
      cta: "Pick up where you left off",
      text: `You're part-way through your Capital Readiness quiz — your ${done} answer${done === 1 ? " is" : "s are"} saved.`,
    };
  }
  const fu = readDraft(FUNDER_QUIZ_STORAGE_KEY);
  if (fu && ((fu.sectors || []).length || fu.ticket || (fu.stages || []).length)) {
    if (fu.completed_at) {
      return {
        kind: "funder-done",
        to: "/signup?role=funder",
        cta: "Sign up to see them",
        text: "Your funder quiz is complete — matching Funder-Ready SMEs are waiting.",
      };
    }
    return {
      kind: "funder",
      to: "/get-started-funder",
      cta: "Pick up where you left off",
      text: "You're part-way through telling us your investment mandate — your answers are saved.",
    };
  }
  return null;
}

/** Bottom banner nudging returning visitors back into an abandoned quiz. */
export const QuizResumeBanner = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [nudge, setNudge] = useState(null);

  useEffect(() => {
    if (loading || user) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY)) return;
    } catch (_) { /* ignore */ }
    setNudge(detectNudge());
  }, [loading, user]);

  if (!nudge) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch (_) { /* ignore */ }
    setNudge(null);
  };

  return (
    <div
      data-testid="quiz-resume-banner"
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6"
      style={{ fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif" }}
    >
      <div className="relative mx-auto flex max-w-3xl flex-col gap-3 rounded-2xl border border-emerald-400/25 bg-[#04302E]/95 px-4 py-4 pr-10 text-white shadow-2xl shadow-black/40 backdrop-blur-md sm:flex-row sm:items-center sm:px-5 sm:pr-5">
        <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
          <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#F59E0B]/15 ring-1 ring-[#F59E0B]/40 sm:h-9 sm:w-9">
            <Sparkles className="h-4 w-4 text-[#F59E0B]" />
          </span>
          <p className="min-w-0 flex-1 text-[13px] leading-snug text-white/90 sm:text-sm" data-testid="quiz-resume-text">
            {nudge.text}
          </p>
        </div>
        <button
          data-testid="quiz-resume-cta"
          onClick={() => nav(nudge.to)}
          className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-[#F59E0B] px-4 py-2.5 text-xs font-bold text-[#04302E] transition-colors hover:bg-amber-400 sm:w-auto sm:py-2"
        >
          {nudge.cta} <ArrowRight className="h-3.5 w-3.5" />
        </button>
        <button
          data-testid="quiz-resume-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute right-2 top-2 rounded-md p-1.5 text-white/50 transition-colors hover:bg-white/10 hover:text-white sm:static"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
